// src/services/dashboard/BookMetadataService.js

/**
 * Service to fetch book metadata from Google Books and OpenLibrary APIs
 * and normalize it into BooksAtDoorstep format.
 */

// Mapping of language codes to BooksAtDoorstep language strings
const LANGUAGE_MAP = {
  en: "English",
  eng: "English",
  hi: "Hindi",
  hin: "Hindi",
  mr: "Marathi",
  mar: "Marathi",
  gu: "Gujarati",
  guj: "Gujarati",
  gujarati: "Gujarati",
  sa: "Sanskrit",
  san: "Sanskrit",
  ur: "Urdu",
  urd: "Urdu",
  bn: "Bengali",
  ta: "Tamil",
  te: "Telugu",
};

// Supported Genres in BooksAtDoorstep
const GENRE_KEYWORDS = {
  FICTION: ["fiction", "novel", "stories", "classic literature", "prose"],
  NON_FICTION: ["non-fiction", "general", "essay", "reference"],
  MYSTERY: ["mystery", "detective", "crime", "investigation", "suspense"],
  FANTASY: ["fantasy", "magic", "mythology", "folklore", "dragon", "wizard"],
  SCIENCE_FICTION: ["science fiction", "sci-fi", "space", "alien", "futuristic", "time travel"],
  BIOGRAPHY: ["biography", "autobiography", "memoir", "life story", "leader"],
  HISTORY: ["history", "historical", "war", "civilization", "ancient", "empire"],
  ROMANCE: ["romance", "love", "relationship", "dating"],
  HORROR: ["horror", "ghost", "paranormal", "scary", "supernatural"],
  POETRY: ["poetry", "poems", "verse", "rhyme"],
  THRILLER: ["thriller", "action", "espionage", "spy", "conspiracy"],
  ADVENTURE: ["adventure", "quest", "exploration", "survival"],
  SELF_HELP: ["self-help", "motivation", "success", "personal growth", "productivity", "psychology", "mindset"],
  GRAPHIC_NOVEL: ["comic", "manga", "graphic novel", "cartoon"],
  PHILOSOPHY: ["philosophy", "ethics", "logic", "existential"],
  RELIGION: ["religion", "spiritual", "spirituality", "hinduism", "islam", "christianity", "buddhism", "theology"],
  EDUCATIONAL: ["education", "textbook", "academic", "study", "learning", "science", "mathematics", "computer"],
  TRAVEL: ["travel", "tourism", "geography", "guidebook"],
  COOKING: ["cooking", "cookbook", "food", "recipes", "culinary"],
  ART: ["art", "design", "photography", "painting", "architecture"],
  DRAMA: ["drama", "plays", "theatre"],
};

const BookMetadataService = {
  /**
   * Cleans an ISBN string (removes dashes, spaces, and handles EAN+5 price extensions)
   */
  cleanIsbn(isbn) {
    if (!isbn) return "";
    let cleaned = String(isbn).replace(/[^0-9X]/gi, "").toUpperCase();

    // If an 18-digit barcode was scanned (EAN-13 + 5-digit price addon), take the first 13 digits
    if (cleaned.length === 18) {
      cleaned = cleaned.slice(0, 13);
    }
    // If a 12-digit UPC was scanned, convert to EAN-13 by prepending 0
    if (cleaned.length === 12) {
      cleaned = "0" + cleaned;
    }

    return cleaned;
  },

  /**
   * Retrieves Google Books API Key from env or localStorage
   */
  getApiKey() {
    try {
      return (
        process.env.REACT_APP_GOOGLE_BOOKS_API_KEY ||
        localStorage.getItem("GOOGLE_BOOKS_API_KEY") ||
        ""
      );
    } catch {
      return "";
    }
  },

  /**
   * Saves Google Books API Key in localStorage
   */
  setApiKey(key) {
    try {
      if (key && key.trim()) {
        localStorage.setItem("GOOGLE_BOOKS_API_KEY", key.trim());
      } else {
        localStorage.removeItem("GOOGLE_BOOKS_API_KEY");
      }
    } catch (e) {
      console.warn("Could not save API key in localStorage:", e);
    }
  },

  /**
   * Fetches metadata for an ISBN using Google Books API with OpenLibrary fallbacks
   */
  async fetchByIsbn(rawIsbn) {
    const isbn = this.cleanIsbn(rawIsbn);
    if (!isbn || (isbn.length !== 10 && isbn.length !== 13)) {
      throw new Error("Invalid ISBN format. Please enter a 10 or 13-digit ISBN.");
    }

    let result = null;
    let isRateLimited = false;

    // 1. Try Google Books API
    try {
      const apiKey = this.getApiKey();
      const keyParam = apiKey ? `&key=${apiKey}` : "";
      const gBooksUrl = `https://www.googleapis.com/books/v1/volumes?q=isbn:${isbn}&maxResults=1${keyParam}`;

      const response = await fetch(gBooksUrl);
      if (response.status === 429) {
        isRateLimited = true;
        console.warn("Google Books API 429 (rate limited without API key). Trying OpenLibrary...");
      } else if (response.ok) {
        const data = await response.json();
        if (data.totalItems > 0 && data.items?.[0]?.volumeInfo) {
          result = this.normalizeGoogleBooks(data.items[0].volumeInfo, isbn);
        }
      }
    } catch (err) {
      console.warn("Google Books API query failed, trying OpenLibrary fallback:", err);
    }

    // 2. Fallback to OpenLibrary (bibkeys data endpoint)
    if (!result) {
      try {
        const olUrl = `https://openlibrary.org/api/books?bibkeys=ISBN:${isbn}&format=json&jscmd=data`;
        const response = await fetch(olUrl);
        if (response.ok) {
          const data = await response.json();
          const bookKey = `ISBN:${isbn}`;
          if (data[bookKey]) {
            result = this.normalizeOpenLibrary(data[bookKey], isbn);
          }
        }
      } catch (err) {
        console.warn("OpenLibrary bibkeys query failed:", err);
      }
    }

    // 3. Fallback to OpenLibrary (search endpoint)
    if (!result) {
      try {
        const olSearchUrl = `https://openlibrary.org/search.json?isbn=${isbn}`;
        const response = await fetch(olSearchUrl);
        if (response.ok) {
          const data = await response.json();
          if (data.numFound > 0 && data.docs?.[0]) {
            result = this.normalizeOpenLibrarySearch(data.docs[0], isbn);
          }
        }
      } catch (err) {
        console.warn("OpenLibrary search query failed:", err);
      }
    }

    if (!result) {
      if (isRateLimited && !this.getApiKey()) {
        throw new Error(
          `Google Books rate-limited anonymous lookup. Enter your free Google Books API Key in Scanner settings or fill remaining details manually.`
        );
      }
      throw new Error(`No book found online for ISBN: ${isbn}. You can still fill the details manually.`);
    }

    return result;
  },

  /**
   * Normalizes Google Books volumeInfo into our standard shape
   */
  normalizeGoogleBooks(vol, queryIsbn) {
    const authors = vol.authors && vol.authors.length > 0 ? vol.authors : ["Unknown Author"];
    const authorName = authors[0].trim();

    // Extract ISBN-10 and ISBN-13
    let isbn10 = "";
    let isbn13 = "";
    if (vol.industryIdentifiers) {
      vol.industryIdentifiers.forEach((id) => {
        const val = this.cleanIsbn(id.identifier);
        if (id.type === "ISBN_10" || val.length === 10) isbn10 = val;
        if (id.type === "ISBN_13" || val.length === 13) isbn13 = val;
      });
    }
    if (!isbn10 && queryIsbn.length === 10) isbn10 = queryIsbn;
    if (!isbn13 && queryIsbn.length === 13) isbn13 = queryIsbn;

    // Format published date
    let publishedDate = "";
    if (vol.publishedDate) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(vol.publishedDate)) {
        publishedDate = vol.publishedDate;
      } else if (/^\d{4}-\d{2}$/.test(vol.publishedDate)) {
        publishedDate = `${vol.publishedDate}-01`;
      } else if (/^\d{4}$/.test(vol.publishedDate)) {
        publishedDate = `${vol.publishedDate}-01-01`;
      }
    }

    // Language
    const rawLang = (vol.language || "en").toLowerCase();
    const language = LANGUAGE_MAP[rawLang] || "English";

    // Genre
    const categories = vol.categories || [];
    const genre = this.detectGenre(categories, vol.title, vol.description);

    // Reading age
    const readingAge = this.detectReadingAge(categories, vol.description);

    // Description (must satisfy >= 200 chars for form validation)
    const cleanDesc = (vol.description || "").replace(/<[^>]*>?/gm, "").trim();
    const description = this.ensureMinDescription(
      cleanDesc,
      vol.title,
      authorName,
      genre,
      language,
      vol.publisher
    );

    // Cover Image URL (upgrade to https if needed)
    let coverImageUrl = "";
    if (vol.imageLinks) {
      const img =
        vol.imageLinks.thumbnail ||
        vol.imageLinks.smallThumbnail ||
        vol.imageLinks.medium ||
        vol.imageLinks.large;
      if (img) {
        coverImageUrl = img.replace(/^http:\/\//i, "https://");
      }
    }

    return {
      title: vol.title || "",
      subtitle: vol.subtitle || "",
      authorName: authorName,
      allAuthors: authors,
      publisher: vol.publisher || `${authorName} Publications`,
      publishedDate: publishedDate,
      pages: vol.pageCount || 200,
      isbn10: isbn10,
      isbn13: isbn13,
      language: language,
      genre: genre,
      readingAge: readingAge,
      description: description,
      coverImageUrl: coverImageUrl,
      categories: categories,
      source: "Google Books",
    };
  },

  /**
   * Normalizes OpenLibrary book data into our standard shape
   */
  normalizeOpenLibrary(data, queryIsbn) {
    const authors =
      data.authors && data.authors.length > 0
        ? data.authors.map((a) => a.name)
        : ["Unknown Author"];
    const authorName = authors[0].trim();

    let isbn10 = queryIsbn.length === 10 ? queryIsbn : "";
    let isbn13 = queryIsbn.length === 13 ? queryIsbn : "";
    if (data.identifiers) {
      if (data.identifiers.isbn_10?.[0]) isbn10 = this.cleanIsbn(data.identifiers.isbn_10[0]);
      if (data.identifiers.isbn_13?.[0]) isbn13 = this.cleanIsbn(data.identifiers.isbn_13[0]);
    }

    let publishedDate = "";
    if (data.publish_date) {
      const yearMatch = data.publish_date.match(/\b(19|20)\d{2}\b/);
      if (yearMatch) publishedDate = `${yearMatch[0]}-01-01`;
    }

    const publisher = data.publishers?.[0]?.name || `${authorName} Publications`;
    const subjects = data.subjects?.map((s) => (typeof s === "string" ? s : s.name)) || [];
    const genre = this.detectGenre(subjects, data.title, "");
    const readingAge = this.detectReadingAge(subjects, "");

    const cleanDesc = typeof data.description === "string" ? data.description : data.description?.value || "";
    const description = this.ensureMinDescription(
      cleanDesc,
      data.title,
      authorName,
      genre,
      "English",
      publisher
    );

    const coverImageUrl = data.cover?.large || data.cover?.medium || data.cover?.small || "";

    return {
      title: data.title || "",
      authorName: authorName,
      allAuthors: authors,
      publisher: publisher,
      publishedDate: publishedDate,
      pages: data.number_of_pages || 200,
      isbn10: isbn10,
      isbn13: isbn13,
      language: "English",
      genre: genre,
      readingAge: readingAge,
      description: description,
      coverImageUrl: coverImageUrl,
      categories: subjects,
      source: "OpenLibrary",
    };
  },

  /**
   * Normalizes OpenLibrary Search API document (from /search.json) into our standard shape
   */
  normalizeOpenLibrarySearch(doc, queryIsbn) {
    const authors =
      doc.author_name && doc.author_name.length > 0
        ? doc.author_name
        : ["Unknown Author"];
    const authorName = authors[0].trim();
    const title = doc.title || "";
    const publisher = doc.publisher?.[0] || `${authorName} Publications`;
    const publishedDate = doc.first_publish_year ? `${doc.first_publish_year}-01-01` : "";
    const pages = doc.number_of_pages_median || 200;
    const subjects = doc.subject || [];
    const genre = this.detectGenre(subjects, title, "");
    const readingAge = this.detectReadingAge(subjects, "");

    const description = this.ensureMinDescription(
      "",
      title,
      authorName,
      genre,
      "English",
      publisher
    );

    const coverImageUrl = doc.cover_i
      ? `https://covers.openlibrary.org/b/id/${doc.cover_i}-L.jpg`
      : `https://covers.openlibrary.org/b/isbn/${queryIsbn}-L.jpg`;

    return {
      title,
      authorName,
      allAuthors: authors,
      publisher,
      publishedDate,
      pages,
      isbn10: queryIsbn.length === 10 ? queryIsbn : "",
      isbn13: queryIsbn.length === 13 ? queryIsbn : "",
      language: "English",
      genre,
      readingAge,
      description,
      coverImageUrl,
      categories: subjects.slice(0, 10),
      source: "OpenLibrary",
    };
  },

  /**
   * Detects the closest Genre Enum from categories, title, and description
   */
  detectGenre(categories = [], title = "", description = "") {
    const combined = [...categories, title, description].join(" ").toLowerCase();

    for (const [genre, keywords] of Object.entries(GENRE_KEYWORDS)) {
      for (const kw of keywords) {
        if (combined.includes(kw)) {
          return genre;
        }
      }
    }
    return "FICTION"; // safe default
  },

  /**
   * Detects Reading Age enum: CHILD, PRE_TEEN, TEEN, ADULT
   */
  detectReadingAge(categories = [], description = "") {
    const combined = [...categories, description].join(" ").toLowerCase();
    if (combined.includes("children") || combined.includes("kid") || combined.includes("picture book")) {
      return "CHILD";
    }
    if (combined.includes("juvenile") || combined.includes("middle grade") || combined.includes("pre-teen")) {
      return "PRE_TEEN";
    }
    if (combined.includes("young adult") || combined.includes("teen") || combined.includes("ya ")) {
      return "TEEN";
    }
    return "ADULT";
  },

  /**
   * Guarantees description meets the minLength: 200 validation rule
   */
  ensureMinDescription(originalDesc, title, author, genre, language, publisher) {
    if (originalDesc && originalDesc.length >= 200) {
      return originalDesc;
    }

    const base = originalDesc ? `${originalDesc.trim()} ` : "";
    const padding = `"${title}" is an inspiring and acclaimed book by ${author}. Published in ${language} under the ${genre} genre by ${publisher || "esteemed publishers"}, this title offers readers a thoroughly engaging and thought-provoking reading experience with insightful narrative and timeless literary value. Highly recommended for book lovers.`;

    const combined = `${base}${padding}`.trim();
    return combined.length >= 200 ? combined : combined.padEnd(210, ".");
  },

  /**
   * Converts an image URL to a File object for multipart form upload
   */
  async urlToFile(url, filename = "book-cover.jpg") {
    if (!url) return null;
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      const blob = await res.blob();
      let finalFilename = filename;
      const ext = blob.type.includes("png")
        ? ".png"
        : blob.type.includes("webp")
        ? ".webp"
        : ".jpg";
      if (!finalFilename.match(/\.(jpg|jpeg|png|webp|gif)$/i)) {
        finalFilename = `${finalFilename}${ext}`;
      }
      return new File([blob], finalFilename, { type: blob.type || "image/jpeg" });
    } catch (err) {
      console.warn("Could not convert image URL to File:", err);
      return null;
    }
  },

  /**
   * Generates a high-quality branded author avatar File (with gradient & initials)
   * used as a robust fallback to satisfy backend multipart image requirements.
   */
  async generateAuthorAvatar(name = "Author") {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = 300;
      canvas.height = 300;
      const ctx = canvas.getContext("2d");

      // Warm gradient background
      const grad = ctx.createLinearGradient(0, 0, 300, 300);
      grad.addColorStop(0, "#FF864A");
      grad.addColorStop(1, "#DD5A1E");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, 300, 300);

      // Clean author initials
      const initials = (name || "Author")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0].toUpperCase())
        .join("") || "A";

      ctx.fillStyle = "#FFFFFF";
      ctx.font = "bold 110px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(initials, 150, 150);

      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            resolve(null);
            return;
          }
          const cleanName = (name || "author").replace(/[^a-zA-Z0-9]/g, "_").slice(0, 30);
          const file = new File([blob], `${cleanName}_avatar.png`, {
            type: "image/png",
          });
          resolve(file);
        }, "image/png");
      });
    } catch {
      return null;
    }
  },

  /**
   * Fetches author profile (portrait image URL and biography) using Wikipedia REST API and OpenLibrary.
   * @param {string} authorName
   * @returns {Promise<{authorName: string, imageUrl: string|null, thumbnailUrl: string|null, bio: string, source: string}|null>}
   */
  async fetchAuthorProfile(authorName) {
    if (!authorName || !authorName.trim()) return null;
    const cleanName = authorName
      .trim()
      .replace(/^(by|dr\.|prof\.|mr\.|mrs\.|ms\.)\s+/i, "")
      .trim();

    // 1. Try direct Wikipedia REST summary
    try {
      const summaryUrl = `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(cleanName)}`;
      const res = await fetch(summaryUrl, {
        headers: { "User-Agent": "BooksAtDoorstep/1.0 (contact@booksatdoorstep.com)" },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.type === "standard" && (data.thumbnail?.source || data.extract)) {
          return {
            authorName: data.title || cleanName,
            imageUrl: data.originalimage?.source || data.thumbnail?.source || null,
            thumbnailUrl: data.thumbnail?.source || data.originalimage?.source || null,
            bio: data.extract ? data.extract.slice(0, 950) : "",
            source: "Wikipedia",
          };
        }
      }
    } catch (e) {
      console.warn("Wikipedia summary lookup notice:", e);
    }

    // 2. Try Wikipedia Search if direct page not found
    try {
      const searchUrl = `https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${encodeURIComponent(
        cleanName + " author"
      )}&format=json&origin=*`;
      const sRes = await fetch(searchUrl);
      if (sRes.ok) {
        const sData = await sRes.json();
        const topHit = sData.query?.search?.[0];
        if (topHit?.title) {
          const sumUrl = `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(topHit.title)}`;
          const res2 = await fetch(sumUrl);
          if (res2.ok) {
            const d2 = await res2.json();
            if (d2.thumbnail?.source || d2.extract) {
              return {
                authorName: d2.title || cleanName,
                imageUrl: d2.originalimage?.source || d2.thumbnail?.source || null,
                thumbnailUrl: d2.thumbnail?.source || d2.originalimage?.source || null,
                bio: d2.extract ? d2.extract.slice(0, 950) : "",
                source: "Wikipedia",
              };
            }
          }
        }
      }
    } catch (e) {
      console.warn("Wikipedia search lookup notice:", e);
    }

    // 3. Try OpenLibrary Search
    try {
      const olUrl = `https://openlibrary.org/search/authors.json?q=${encodeURIComponent(cleanName)}`;
      const olRes = await fetch(olUrl);
      if (olRes.ok) {
        const olData = await olRes.json();
        const doc = olData.docs?.[0];
        if (doc && doc.key) {
          const coverCheckUrl = `https://covers.openlibrary.org/a/olid/${doc.key}-L.jpg?default=false`;
          let hasPhoto = false;
          try {
            const testRes = await fetch(coverCheckUrl, { method: "HEAD" });
            hasPhoto = testRes.ok;
          } catch {
            hasPhoto = false;
          }

          return {
            authorName: doc.name || cleanName,
            imageUrl: hasPhoto ? `https://covers.openlibrary.org/a/olid/${doc.key}-L.jpg` : null,
            thumbnailUrl: hasPhoto ? `https://covers.openlibrary.org/a/olid/${doc.key}-M.jpg` : null,
            bio: doc.top_work
              ? `Renowned author of "${doc.top_work}" and other celebrated literary works.`
              : "",
            source: "OpenLibrary",
          };
        }
      }
    } catch (e) {
      console.warn("OpenLibrary author lookup notice:", e);
    }

    return null;
  },

  /**
   * Downloads author image and returns a File object suitable for multipart upload.
   * Falls back to canvas-generated initials avatar if image is unavailable.
   */
  async getAuthorImageFile(imageUrl, authorName) {
    if (imageUrl) {
      try {
        const cleanName = (authorName || "author")
          .trim()
          .replace(/[^a-zA-Z0-9]/g, "_")
          .slice(0, 30);
        const file = await this.urlToFile(imageUrl, `${cleanName}_photo.jpg`);
        if (file && file.size > 500) {
          return file;
        }
      } catch (err) {
        console.warn("Failed to download author image file:", err);
      }
    }
    // Fallback to generated branded avatar
    return this.generateAuthorAvatar(authorName);
  },
};

export default BookMetadataService;
